import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';

import { CONTENT_ALIGN_Y, TRANSITION_TYPE } from 'common/constants';
import { BBCommonProps } from 'types/story';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { getExposeDuration, getExposeParams } from 'client/components/common/BuildingBlocks/utils/common';
import AutoHeight from './AutoHeight';
import css from './Content.scss';

type ContentProps = BBCommonProps & {
	exposeDuration?: number;
	exposeEffect?: typeof TRANSITION_TYPE[keyof typeof TRANSITION_TYPE];
	contentAlignY: typeof CONTENT_ALIGN_Y[keyof typeof CONTENT_ALIGN_Y];
	onTransitionEnd: (...args: any[]) => any;
	transitionCSS: any;
};

class Content extends React.Component<ContentProps> {
	contentRef = React.createRef<HTMLDivElement>();

	contentInnerRef = React.createRef<HTMLDivElement>();

	childrenTransitions: any;

	isSelfTransitionComplete: boolean;

	isChildrenTransitionComplete: boolean;

	transitionTimeout?: number;

	onResizeDebounced: any;

	constructor(props, context) {
		super(props, context);

		this.childrenTransitions = props.resetChildrenTransition();
		this.isSelfTransitionComplete = false;
		this.isChildrenTransitionComplete = _.size(this.props.children) === 0;
		this.transitionTimeout = 0;
		this.onResizeDebounced = _.debounce(this.onWindowResize, 50);
	}

	componentDidMount() {
		window.addEventListener('resize', this.onResizeDebounced);
		this.onWindowResize();
	}

	componentDidUpdate(prevProps: any) {
		const { duration, delay, effect } = this.exposeParams;

		if (effect && this.props.in !== prevProps.in) {
			this.isSelfTransitionComplete = false;
			clearTimeout(this.transitionTimeout);
			this.transitionTimeout = window.setTimeout(
				() => {
					this.isSelfTransitionComplete = true;
					if (this.isSelfTransitionComplete && this.isChildrenTransitionComplete) {
						this.props.onTransitionEnd(this.props);
					}
				},
				effect ? duration + delay : 0
			);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onResizeDebounced);
	}

	get exposeParams() {
		return getExposeParams({
			exposeEffect: this.props.exposeEffect,
			exposeDuration: this.props.exposeDuration,
			bbType: this.props.type,
			in: this.props.in,
		});
	}

	onWindowResize = () => {
		if (!this.contentRef.current) {
			return;
		}
		this.contentRef.current.style.minHeight = `${window.innerHeight}px`;
	};

	onChildTransitionEnd = ({ _id, type }) => {
		if (this.childrenTransitions[_id]) {
			return;
		}

		this.childrenTransitions[_id] = true;

		if (_.values(this.childrenTransitions).every(isComplete => !!isComplete)) {
			this.isChildrenTransitionComplete = true;
			if (this.isSelfTransitionComplete && this.isChildrenTransitionComplete) {
				this.props.onTransitionEnd(this.props);
			}
		}
	};

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps };
		return this.renderDefault(props);
	}

	renderDefault(props: any = {}) {
		const { uiConfig, stateAttrs, eventListeners, transitionCSS } = this.props;
		const { duration, delay } = this.exposeParams;

		return (
			<div className={classNames(css.contentW, css[`align-${this.props.contentAlignY}`])} ref={this.contentRef}>
				<div className={css.contentI}>
					<div
						{...uiConfig.nodeProps}
						{...stateAttrs}
						{...eventListeners}
						{...props}
						className={classNames(
							transitionCSS,
							css.content,
							uiConfig.nodeProps.className,
							props.className
						)}
						style={{
							...props.style,
							...uiConfig.nodeProps.style,
							...(transitionCSS
								? { transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` }
								: null),
						}}
						ref={this.contentInnerRef}
					>
						<AutoHeight
							uiConfig={this.props.uiConfig}
							contentAlignY={this.props.contentAlignY}
							contentInnerRef={this.contentInnerRef}
						>
							{React.Children.map(this.props.children, (child: any) => {
								return React.cloneElement(child, { onTransitionEnd: this.onChildTransitionEnd });
							})}
						</AutoHeight>
					</div>
				</div>
			</div>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

const mapState = (state, ownProps) => ({
	exposeDuration: getExposeDuration(
		_.get(state, `story.storyVersions.latest.settings.cards.${state.card._id}.expose.duration`)
	),
	exposeEffect: _.get(ownProps.uiConfig, 'expose.effect'),
});

export default connect(mapState)(withCardTransitionContext(Content));
